import { useEffect } from 'react';
import { addLocale, useLocale, LocaleData } from 'ttag';
import { TrainingState, useTrainingStore } from '~/training/state';
import { select } from '../select';
import { resolveLocale } from './resolveLocale';
import { translations } from './translations';
import { useStore } from './useStore';

const selector = (s: TrainingState) =>
  select(s, 'setTextLocale', 'textLocale', 'languageAutoDetect');

/**
 * Ensures that the appropriate translation is loaded and set based on the current locale
 */
export function useLoadTranslation() {
  const { textLocale, setTextLocale, languageAutoDetect } =
    useTrainingStore(selector);
  const { setLoaded } = useStore();

  useEffect(() => {
    if (!textLocale && languageAutoDetect) {
      // If no culture is set, use the browser's preference.
      // This hook will then be automatically called again.
      setTextLocale(window.navigator.language);
      return;
    }

    // Resolve the current culture to something the UI supports
    const resolved = resolveLocale(textLocale);

    // If language is English then it's easy because it's the default so we just make sure it's set
    if (resolved === 'en') {
      setTimeout(() => {
        useLocale('en');
        setLoaded();
      });
      return;
    }

    // If another language is set, then we must load it dynamically first and set it
    translations[resolved]().then((t: { default: LocaleData }) => {
      addLocale(resolved, t.default);
      useLocale(resolved);
      setLoaded();
    });
  }, [textLocale]);
}
